import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { DESIGN_STATUS_LABELS, PN_TYPE_LABELS, type DesignProject, type DesignRevision, type DesignPartNumber } from "@/types/design";

const NAVY: [number, number, number] = [0, 51, 153];
const GRAY: [number, number, number] = [100, 100, 100];

const INPUT_SECTIONS: { key: string; label: string }[] = [
  { key: "herramientas", label: "Herramientas disponibles" },
  { key: "material_disponible", label: "Material disponible" },
  { key: "especificaciones_pieza", label: "Especificaciones (Pieza)" },
  { key: "especificaciones_master", label: "Especificaciones (Master)" },
  { key: "especificaciones_molde", label: "Especificaciones (Molde)" },
  { key: "especificaciones_utillaje", label: "Especificaciones (Utillajes)" },
  { key: "legales", label: "Entradas legales" },
  { key: "normativas", label: "Entradas normativas" },
  { key: "items_criticos_diseno", label: "Ítems críticos de diseño" },
  { key: "items_criticos_fabricacion", label: "Ítems críticos de fabricación" },
  { key: "amfe", label: "AMFE" },
];

interface Args {
  project: DesignProject;
  revisions: DesignRevision[];
  pns: DesignPartNumber[];
  customerName?: string;
  directorName?: string;
  leadName?: string;
  profMap?: Map<string, string>;
}

export function generateDesignProjectPdf({ project, revisions, pns, customerName, directorName, leadName, profMap }: Args): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 15;
  let y = M;

  const ensure = (h: number) => { if (y + h > H - 15) { doc.addPage(); y = M; } };
  const sectionTitle = (n: string, t: string) => {
    ensure(12);
    doc.setFillColor(...NAVY); doc.rect(M, y, W - 2 * M, 7, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(10.5); doc.setFont("helvetica", "bold");
    doc.text(`${n}. ${t}`, M + 2, y + 5);
    doc.setTextColor(0, 0, 0); y += 11;
  };
  const textBlock = (label: string, value?: string | null) => {
    const v = (value && String(value).trim()) || "—";
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5);
    ensure(6); doc.text(label, M, y); y += 4;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(v, W - 2 * M);
    ensure(lines.length * 4 + 2);
    doc.text(lines, M, y); y += lines.length * 4 + 2;
  };

  // Header
  doc.setFillColor(...NAVY); doc.rect(0, 0, W, 22, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(15); doc.setFont("helvetica", "bold");
  doc.text("PROYECTO DE DISEÑO", M, 10);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text("M_05.14 · ATHENEA MES", M, 16);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleDateString("es-ES"), W - M, 10, { align: "right" });
  doc.setTextColor(0, 0, 0); y = 30;

  doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.text(project.name, M, y); y += 6;
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...GRAY);
  doc.text(`${project.code ? "Código: " + project.code + "  ·  " : ""}Estado: ${DESIGN_STATUS_LABELS[project.status]}  ·  Cliente: ${customerName || "—"}`, M, y);
  doc.setTextColor(0, 0, 0); y += 8;

  sectionTitle("1", "Solicitud de diseño");
  textBlock("Solicitud:", project.request_summary);

  sectionTitle("2", "Participantes");
  textBlock("Propietario del proyecto:", project.owner);
  textBlock("Proveedor de soluciones:", project.solution_provider);
  textBlock("Director de proyecto:", directorName);
  textBlock("Responsable de diseño:", leadName);

  sectionTitle("3", "Cronograma y planificación");
  textBlock("Inicio / Fin:", `${project.start_date || "—"}  →  ${project.end_date || "—"}`);
  textBlock("Notas:", project.schedule_notes);

  sectionTitle("4", "Entradas");
  const inp: any = project.inputs || {};
  INPUT_SECTIONS.forEach((s) => { if (inp[s.key]) textBlock(s.label + ":", inp[s.key]); });

  const pnById = new Map((pns || []).map((p) => [p.id, p] as const));
  sectionTitle("5", "Revisiones del diseño");
  if (revisions.length) {
    autoTable(doc, {
      startY: y, margin: { left: M, right: M },
      head: [["REV", "PN", "Fecha", "Estado", "Cambios", "Responsable"]],
      body: revisions.map((r) => [
        String(r.revision_number).padStart(2, "0"),
        (r as any).design_part_number_id ? (pnById.get((r as any).design_part_number_id)?.pn_code || "—") : "—",
        r.revision_date || "—",
        r.status === "approved" ? "Aprobada" : "Borrador",
        r.changes || "—",
        r.responsible_id ? (profMap?.get(r.responsible_id) || "—") : "—",
      ]),
      styles: { fontSize: 7.5, cellPadding: 1.5 }, headStyles: { fillColor: NAVY },
      columnStyles: { 1: { cellWidth: 38 }, 4: { cellWidth: 60 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  } else { textBlock("", "Sin revisiones."); }

  sectionTitle("6", "Validación del diseño / Part Numbers");
  if (pns.length) {
    autoTable(doc, {
      startY: y, margin: { left: M, right: M },
      head: [["PN", "Tipo", "PN cliente", "Nombre", "Validado", "Validado por"]],
      body: pns.map((p) => [
        p.pn_code, PN_TYPE_LABELS[p.pn_type] || String(p.pn_type),
        p.client_part_pn || "—", p.name || "—", p.validated ? "Sí" : "No",
        (p as any).validated_by ? (profMap?.get((p as any).validated_by) || "—") : "—",
      ]),
      styles: { fontSize: 7.5, cellPadding: 1.5 }, headStyles: { fillColor: NAVY },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  } else { textBlock("", "Sin PN generados."); }

  sectionTitle("7", "Mediciones y validación final");
  const meas: any = (project as any).measurements || {};
  ["master", "molde", "pieza"].forEach((k) => {
    const m = meas[k];
    const label = k.charAt(0).toUpperCase() + k.slice(1);
    textBlock(`${label}:`, m ? `[${(m.status || "pendiente").toUpperCase()}] ${m.notes || ""}` : "—");
  });

  // Footer
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7); doc.setTextColor(...GRAY);
    doc.text("Athenea MES · Proyecto de Diseño M_05.14", M, H - 8);
    doc.text(`Página ${i}/${pages}`, W - M, H - 8, { align: "right" });
  }
  return doc;
}

export function downloadDesignProjectPdf(args: Args) {
  const doc = generateDesignProjectPdf(args);
  const safe = (args.project.code || args.project.name || "proyecto").replace(/[^a-zA-Z0-9_-]/g, "_");
  doc.save(`Proyecto_Diseno_${safe}.pdf`);
}
